/**
 * Recording what you just did, so a report can say where and on what.
 *
 * "THIS BUTTON ISN'T WORKING" IS NOT ACTIONABLE AND A VIDEO OF IT BARELY IS.
 * What makes a report answerable is the route, the control that was clicked,
 * the build that was running, and whatever the console and the network said at
 * that moment. All four are free to collect while you are clicking, and
 * impossible to reconstruct afterwards. So this collects them as you go, and
 * the words you add at the end are the only part you have to write.
 *
 * NOT SESSION REPLAY, AND DELIBERATELY NOT BUILT ON IT. src/replay records
 * visitors on published pages against a signed manifest of publisher bindings,
 * behind consent, with the DOM masked: that narrowness is the privacy promise
 * made to other people's visitors and it should not be loosened to serve this.
 * This is the opposite case. It is you, on your own admin app, recording on
 * purpose, for as long as you choose, and it stays on your machine until you
 * decide to send it.
 *
 * WHAT IT NEVER RECORDS: the value of any field. A dashboard is full of other
 * people's names, invoices and addresses, and a report that quietly carried
 * them off the machine would be a worse problem than the bug it described. It
 * records that a field was filled, and what the field is called.
 */

/** A still is a base64 JPEG with no data: prefix, or absent when the screen was not shared. */
export interface FeedbackStep {
  /** Milliseconds since the recording started. */
  at: number;
  kind: "click" | "field" | "route" | "error" | "request";
  /** Where it happened: pathname and search, never the origin. */
  route: string;
  target?: {
    tag: string;
    role: string | null;
    /** What a person would call it: the accessible name, else its text. */
    name: string;
    /** Enough of a path to find it again in the source. */
    selector: string;
  };
  /** The message, for an error or a failed request. */
  detail?: string;
  /** The still, base64 JPEG with no data: prefix, while it is still in this browser. */
  shot?: string;
  /**
   * The still's name once it is stored, made here so it never changes.
   *
   * The server keys stills by this id rather than by the step's position, so a
   * guide can be reordered and every picture stays with its own step.
   */
  shotId?: string;
}

export interface FeedbackReport {
  version: 1;
  startedAt: number;
  endedAt: number;
  where: {
    href: string;
    /** The built entry file, which names the exact build without needing a version endpoint. */
    build: string | null;
    userAgent: string;
    viewport: string;
  };
  /** What you wanted instead. Written at the end, in your words. */
  note: string;
  steps: FeedbackStep[];
}

const clip = (s: string, n = 120) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);

/** What a person would call this control, in the order a screen reader would try. */
function accessibleName(el: Element): string {
  const aria = el.getAttribute("aria-label");
  if (aria?.trim()) return clip(aria.trim());
  const labelledBy = el.getAttribute("aria-labelledby");
  if (labelledBy) {
    const by = labelledBy
      .split(/\s+/)
      .map((id) => document.getElementById(id)?.textContent?.trim() ?? "")
      .filter(Boolean)
      .join(" ");
    if (by) return clip(by);
  }
  if (el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement) {
    const labels = (el as HTMLInputElement).labels;
    const label = labels && labels.length ? labels[0]!.textContent?.trim() : "";
    if (label) return clip(label);
    const placeholder = el.getAttribute("placeholder");
    if (placeholder?.trim()) return clip(placeholder.trim());
    if (el.name) return clip(el.name);
  }
  const title = el.getAttribute("title");
  if (title?.trim()) return clip(title.trim());
  const text = (el.textContent ?? "").replace(/\s+/g, " ").trim();
  return text ? clip(text) : `<${el.tagName.toLowerCase()}>`;
}

/**
 * A short path to the element, preferring what survives a re-render.
 *
 * An id or a data-testid is stable and stops the path there. Everything else
 * walks up at most four levels, because a forty-level Tailwind path is neither
 * readable by a person nor useful to anything looking for the component.
 */
function selectorFor(el: Element): string {
  const parts: string[] = [];
  let node: Element | null = el;
  for (let depth = 0; node && depth < 4; depth++) {
    const testId = node.getAttribute("data-testid") ?? node.getAttribute("data-test");
    if (testId) {
      parts.unshift(`[data-testid="${testId}"]`);
      break;
    }
    if (node.id) {
      parts.unshift(`#${node.id}`);
      break;
    }
    let part = node.tagName.toLowerCase();
    const parent: Element | null = node.parentElement;
    if (parent) {
      const sameTag = Array.from(parent.children).filter((c) => c.tagName === node!.tagName);
      if (sameTag.length > 1) part += `:nth-of-type(${sameTag.indexOf(node) + 1})`;
    }
    parts.unshift(part);
    node = parent;
  }
  return parts.join(" > ");
}

/** The control a click was aimed at, not the <span> inside it that received the event. */
const controlFor = (el: Element): Element =>
  el.closest("button, a, [role='button'], [role='link'], [role='tab'], [role='menuitem'], input, select, textarea, label, summary") ?? el;

const route = () => `${location.pathname}${location.search}`;

/**
 * The recorder's own controls are not part of what you did.
 *
 * Without this, pressing Stop is the last step of every report, and the panel's
 * own buttons land in the middle of one whenever you glance at it. Anything
 * inside the recorder carries this attribute and is skipped.
 */
export const FEEDBACK_UI_ATTR = "data-bloxx-feedback";
const ours = (el: Element) => !!el.closest(`[${FEEDBACK_UI_ATTR}]`);

/**
 * Which build was running, so a report names the code rather than the day.
 *
 * The production entry is content hashed (`/assets/index-41vaqsPz.js`), which
 * identifies the deploy exactly and costs nothing to read. A dev server has no
 * such file and serves the source module instead, so that is reported as it is
 * rather than as null: "dev" is a useful answer and an empty field is not.
 */
function buildId(): string | null {
  const sources = Array.from(document.querySelectorAll("script[src]")).map((s) => s.getAttribute("src") ?? "");
  const hashed = sources.find((src) => /\/assets\/index-[^/]+\.js$/.test(src));
  if (hashed) return hashed.split("/").pop() ?? null;
  const entry = sources.find((src) => src.includes("/src/main."));
  return entry ? `dev (${entry})` : null;
}

export interface CaptureHandle {
  stop(): FeedbackReport;
  /** Steps so far, so the recorder can show a count while it runs. */
  steps(): FeedbackStep[];
  /** Whether stills are actually being taken, which the screen-share prompt decides. */
  hasStills(): boolean;
}

export interface CaptureOptions {
  /** Ask to share the tab and keep a still per click. Chrome and Edge only. */
  stills?: boolean;
  /** Called whenever a step lands, so the UI can update. */
  onStep?: (steps: FeedbackStep[]) => void;
}

/** Where a step goes, and whether this listener set may ask for a screenshot. */
interface Sink {
  push(step: Omit<FeedbackStep, "at" | "route">, wantsShot: boolean): void;
}

/**
 * Wire up every listener once, so the deliberate recorder and the rolling
 * buffer below watch for exactly the same things.
 *
 * THEY MUST NEVER BOTH BE ATTACHED. Both patch `fetch` and `console.error`, so
 * a second set on top of the first wraps the first: every failed request lands
 * twice and unpatching restores a patched function. The component stops the
 * buffer before it starts a recording and starts it again afterwards.
 */
function attachListeners(sink: Sink): () => void {
  const cleanups: Array<() => void> = [];

  const onClick = (e: Event) => {
    const el = e.target instanceof Element ? controlFor(e.target) : null;
    if (!el || ours(el)) return;
    sink.push(
      { kind: "click", target: { tag: el.tagName.toLowerCase(), role: el.getAttribute("role"), name: accessibleName(el), selector: selectorFor(el) } },
      true,
    );
  };
  document.addEventListener("click", onClick, true);
  cleanups.push(() => document.removeEventListener("click", onClick, true));

  // THE FIELD, NEVER ITS VALUE. See the note at the top of this file.
  const onChange = (e: Event) => {
    const el = e.target;
    if (!(el instanceof HTMLInputElement || el instanceof HTMLSelectElement || el instanceof HTMLTextAreaElement)) return;
    if (ours(el)) return;
    const kind = el instanceof HTMLInputElement ? el.type : el.tagName.toLowerCase();
    sink.push(
      {
        kind: "field",
        detail: kind === "checkbox" || kind === "radio" ? `${kind} toggled` : "filled in",
        target: { tag: el.tagName.toLowerCase(), role: el.getAttribute("role"), name: accessibleName(el), selector: selectorFor(el) },
      },
      false,
    );
  };
  document.addEventListener("change", onChange, true);
  cleanups.push(() => document.removeEventListener("change", onChange, true));

  // A SPA changes route without an event, so the history methods are wrapped.
  let last = route();
  const noteRoute = () => {
    const now = route();
    if (now !== last) {
      last = now;
      sink.push({ kind: "route" }, true);
    }
  };
  const push = history.pushState, replace = history.replaceState;
  history.pushState = function (...args: Parameters<typeof history.pushState>) {
    const out = push.apply(this, args);
    setTimeout(noteRoute, 0);
    return out;
  };
  history.replaceState = function (...args: Parameters<typeof history.replaceState>) {
    const out = replace.apply(this, args);
    setTimeout(noteRoute, 0);
    return out;
  };
  window.addEventListener("popstate", noteRoute);
  cleanups.push(() => {
    history.pushState = push;
    history.replaceState = replace;
    window.removeEventListener("popstate", noteRoute);
  });

  const onError = (e: ErrorEvent) => sink.push({ kind: "error", detail: clip(e.message, 300) }, false);
  const onRejection = (e: PromiseRejectionEvent) => sink.push({ kind: "error", detail: clip(`unhandled rejection: ${String(e.reason)}`, 300) }, false);
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  cleanups.push(() => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onRejection);
  });

  const consoleError = console.error;
  console.error = (...args: unknown[]) => {
    sink.push({ kind: "error", detail: clip(args.map((a) => (a instanceof Error ? a.message : String(a))).join(" "), 300) }, false);
    consoleError.apply(console, args);
  };
  cleanups.push(() => {
    console.error = consoleError;
  });

  // Only the requests that FAILED. A log of every successful call is noise, and
  // the interesting ones are exactly the ones nobody saw fail.
  const fetcher = window.fetch;
  window.fetch = async (...args: Parameters<typeof fetch>) => {
    const url = () => (typeof args[0] === "string" ? args[0] : args[0] instanceof Request ? args[0].url : String(args[0]));
    try {
      const response = await fetcher(...args);
      if (!response.ok) sink.push({ kind: "request", detail: `${response.status} ${clip(url(), 160)}` }, false);
      return response;
    } catch (e) {
      sink.push({ kind: "request", detail: `failed ${clip(url(), 160)}: ${String(e)}` }, false);
      throw e;
    }
  };
  cleanups.push(() => {
    window.fetch = fetcher;
  });

  return () => cleanups.forEach((fn) => fn());
}

/** What the report says about itself, the same for both ways of making one. */
const whereNow = () => ({
  href: location.href,
  build: buildId(),
  userAgent: navigator.userAgent,
  viewport: `${window.innerWidth}x${window.innerHeight}`,
});

/**
 * Start recording. Returns a handle whose `stop` hands back the report.
 *
 * The screen share is asked for FIRST and inside the original click, because a
 * browser only allows the picker straight off a user gesture: anything awaited
 * before it spends that permission and the prompt never appears.
 */
export async function startCapture(options: CaptureOptions = {}): Promise<CaptureHandle> {
  const startedAt = Date.now();
  const steps: FeedbackStep[] = [];

  let stream: MediaStream | null = null;
  let frame: HTMLVideoElement | null = null;
  if (options.stills && navigator.mediaDevices?.getDisplayMedia) {
    try {
      stream = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 4 }, audio: false });
      frame = document.createElement("video");
      frame.muted = true;
      frame.srcObject = stream;
      await frame.play().catch(() => undefined);
    } catch {
      // Declined, or a browser without it. The report is still worth having.
      stream = null;
      frame = null;
    }
  }

    /** Eight hex characters, the shape the server accepts as a still's name. */
  const shotId = () => [...crypto.getRandomValues(new Uint8Array(4))].map((b) => b.toString(16).padStart(2, "0")).join("");

  function still(): string | undefined {
    if (!frame || !frame.videoWidth) return undefined;
    try {
      const canvas = document.createElement("canvas");
      canvas.width = Math.min(1440, frame.videoWidth);
      canvas.height = Math.round(canvas.width * (frame.videoHeight / frame.videoWidth));
      canvas.getContext("2d")?.drawImage(frame, 0, 0, canvas.width, canvas.height);
      return canvas.toDataURL("image/jpeg", 0.7).split(",")[1];
    } catch {
      return undefined;
    }
  }

  const detach = attachListeners({
    push(step, wantsShot) {
      // 300 steps is a very long session and a very large file; past that the
      // report stops growing rather than becoming one nobody can open.
      if (steps.length >= 300) return;
      const shot = wantsShot ? still() : undefined;
      steps.push({ at: Date.now() - startedAt, route: route(), ...step, ...(shot ? { shot, shotId: shotId() } : {}) });
      options.onStep?.(steps.slice());
    },
  });

  return {
    steps: () => steps.slice(),
    hasStills: () => !!frame,
    stop() {
      detach();
      stream?.getTracks().forEach((t) => t.stop());
      frame = null;
      return { version: 1, startedAt, endedAt: Date.now(), where: whereNow(), note: "", steps };
    },
  };
}

/** How far back a retrospective capture reaches, and how much of it is kept. */
export const BUFFER_SECONDS = 90;
const BUFFER_STEPS = 60;

export interface BufferHandle {
  /** Everything still inside the window, re-based so the first step is zero. */
  take(): FeedbackReport;
  /** Errors and failed requests still in the window, for the "something went wrong" mark. */
  trouble(): number;
  stop(): void;
}

/**
 * Always watching, keeping only the last minute and a half.
 *
 * YOU NOTICE A BUG AFTER IT HAPPENS. "Start recording and do it again" loses
 * the occurrence you actually saw, and half the time it will not happen again
 * on demand. So the last ninety seconds are always in memory and the button
 * keeps what just happened rather than asking you to reproduce it.
 *
 * NO SCREENSHOTS HERE, and not as an oversight: stills need a shared screen,
 * and a page that holds a screen share open all day so it might one day catch
 * something is not a page anybody should run. Structure is what makes a bug
 * report answerable anyway. When you want pictures, record deliberately.
 *
 * It holds nothing but the window: steps older than it are dropped as they age
 * out, so this never grows and nothing is kept that you did not ask to keep.
 */
export function startBuffer(): BufferHandle {
  const startedAt = Date.now();
  let steps: Array<FeedbackStep & { wall: number }> = [];

  const trim = () => {
    const floor = Date.now() - BUFFER_SECONDS * 1000;
    if (steps.length > BUFFER_STEPS || (steps[0] && steps[0].wall < floor)) {
      steps = steps.filter((s) => s.wall >= floor).slice(-BUFFER_STEPS);
    }
  };

  const detach = attachListeners({
    push(step) {
      const wall = Date.now();
      steps.push({ at: wall - startedAt, route: route(), ...step, wall });
      trim();
    },
  });

  return {
    trouble() {
      trim();
      return steps.filter((s) => s.kind === "error" || s.kind === "request").length;
    },
    take() {
      trim();
      const kept = steps.slice();
      const base = kept[0]?.wall ?? Date.now();
      return {
        version: 1,
        startedAt: base,
        endedAt: Date.now(),
        where: whereNow(),
        note: "",
        steps: kept.map(({ wall, ...step }) => ({ ...step, at: wall - base })),
      };
    },
    stop() {
      detach();
      steps = [];
    },
  };
}

const clock = (ms: number) => {
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

/**
 * The report as Markdown, for pasting straight into a Claude session.
 *
 * Text only: stills live in the JSON, because an image cannot ride along on the
 * clipboard as text and a report that half-pastes is worse than one that says
 * plainly where the pictures are.
 */
export function asMarkdown(report: FeedbackReport): string {
  const lines: string[] = [];
  lines.push(`# Feedback from ${new Date(report.startedAt).toLocaleString()}`);
  lines.push("");
  if (report.note.trim()) {
    lines.push("## What I wanted");
    lines.push(report.note.trim());
    lines.push("");
  }
  lines.push("## Where");
  lines.push(`- URL: ${report.where.href}`);
  lines.push(`- Build: ${report.where.build ?? "unknown"}`);
  lines.push(`- Viewport: ${report.where.viewport}`);
  lines.push(`- Browser: ${report.where.userAgent}`);
  lines.push("");
  lines.push("## What I did");
  if (!report.steps.length) lines.push("_Nothing was recorded._");
  for (const step of report.steps) {
    const at = clock(step.at);
    if (step.kind === "click") {
      lines.push(`- \`${at}\` **clicked** "${step.target?.name}" on \`${step.route}\``);
      if (step.target?.selector) lines.push(`  - \`${step.target.selector}\``);
    } else if (step.kind === "field") {
      lines.push(`- \`${at}\` ${step.detail} "${step.target?.name}" on \`${step.route}\` (value not recorded)`);
    } else if (step.kind === "route") {
      lines.push(`- \`${at}\` **went to** \`${step.route}\``);
    } else if (step.kind === "error") {
      lines.push(`- \`${at}\` **error:** ${step.detail}`);
    } else {
      lines.push(`- \`${at}\` **request failed:** ${step.detail}`);
    }
  }
  const shots = report.steps.filter((s) => s.shot).length;
  if (shots) {
    lines.push("");
    lines.push(`${shots} screenshot${shots === 1 ? "" : "s"} are in the downloaded JSON, one per step, base64 JPEG in \`steps[].shot\`.`);
  }
  return lines.join("\n");
}
